import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Check,
  Search,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { useIsMobile } from '@/hooks/useIsMobile'
import {
  getApiAdminCheckinsQueryKey,
  getApiAdminGuestQueryKey,
  getApiTablesMasterByIdGuestsOptions,
  getApiTablesMasterByIdGuestsQueryKey,
  getApiTablesMasterByIdOptions,
  getApiTablesMasterQueryKey,
  postApiTablesBulkMutation,
  postApiTablesMutation,
} from '@/lib/api/client/@tanstack/react-query.gen'
import type { TableGuestItem } from '@/lib/api/client/types.gen'

interface SelectedGuestInfo {
  targetId: string
  fullName: string
  followers: number
  isExisting?: boolean
}

interface GuestItemData {
  id: string
  fullName: string
  followers?: number
  expectedFollowers?: number
  status?: string
  tableAssignment?: {
    id?: string
    tableId?: string
    tableNumber?: string
    zone?: string
  } | null
}

interface ApiError {
  body?: {
    error?: string
  }
  message?: string
}

import { useInfiniteMasterGuestList } from '../Guests/useMasterGuestList'

interface AssignGuestDialogProps {
  isOpen: boolean
  onClose: () => void
  tableId: string
  tableNumber: string
}

export const AssignGuestDialog = ({
  isOpen,
  onClose,
  tableId,
  tableNumber,
}: AssignGuestDialogProps) => {
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const {
    searchQuery,
    setSearchQuery,
    guests: currentList,
    isLoading: isLoadingAll,
    loadMoreRef,
    isFetchingNextPage,
  } = useInfiniteMasterGuestList()

  const lastTableIdRefInternal = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      lastTableIdRefInternal.current = null
    }
  }, [isOpen])

  // Track selections across tabs: Map<"type:id", { id, type, name, followers }>
  const [selectedMap, setSelectedMap] = useState<
    Map<string, SelectedGuestInfo>
  >(new Map())

  // Fetch table details for capacity
  const { data: tableData } = useQuery({
    ...getApiTablesMasterByIdOptions({ path: { id: tableId } }),
    enabled: !!tableId && isOpen,
  })

  const capacity = tableData?.capacity || 0

  // Fetch existing guests in this table
  const { data: existingData, isLoading: isLoadingExisting } = useQuery({
    ...getApiTablesMasterByIdGuestsOptions({ path: { id: tableId } }),
    enabled: !!tableId && isOpen,
  })

  // Reset selectedMap when tableId changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedMap(new Map())
    }
  }, [tableId, isOpen])

  // Initialize selections with existing guests (once per table open)
  useEffect(() => {
    if (
      isOpen &&
      existingData?.guests &&
      lastTableIdRefInternal.current !== tableId
    ) {
      const map = new Map<string, SelectedGuestInfo>()
      existingData.guests.forEach((g: TableGuestItem) => {
        const guest = g.masterGuest
        if (guest) {
          map.set(guest.id, {
            targetId: guest.id,
            fullName: guest.fullName,
            followers: guest.expectedFollowers || 1,
            isExisting: true,
          })
        }
      })
      setSelectedMap(map)
      lastTableIdRefInternal.current = tableId
    }
  }, [existingData, tableId, isOpen])

  const totalSelectedFollowers = useMemo(() => {
    let total = 0
    selectedMap.forEach((g) => {
      total += g.followers || 1
    })
    return total
  }, [selectedMap])

  const toggleGuest = (guest: GuestItemData) => {
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (next.has(guest.id)) {
        next.delete(guest.id)
      } else {
        const guestCount = guest.followers || guest.expectedFollowers || 1
        if (totalSelectedFollowers + guestCount > capacity) {
          toast.error(
            `ไม่สามารถเพิ่มได้เนื่องจากที่นั่งเต็ม (โต๊ะนี้รับได้ ${capacity} ท่าน)`,
          )
          return prev
        }
        next.set(guest.id, {
          targetId: guest.id,
          fullName: guest.fullName,
          followers: guest.followers || guest.expectedFollowers || 1,
        })
      }
      return next
    })
  }

  const isSelected = useCallback(
    (guestId: string) => {
      return selectedMap.has(guestId)
    },
    [selectedMap],
  )

  // Filter available guests:
  // 1. Not in selectedMap (already on the right side)
  // 2. Either no table assigned OR assigned to THIS table (to allow moving back and forth)
  const filteredAvailableList = useMemo(() => {
    return currentList.filter((guest) => {
      if (isSelected(guest.id)) return false

      const guestTableId = guest.tableAssignment?.tableId

      // Show if:
      // - No table assigned
      // - Assigned to this table (so we can move them back to Available if unselected)
      return !guestTableId || guestTableId === tableId
    })
  }, [currentList, tableId, isSelected])

  // Single Assignment Mutation (for mobile quick add)
  const setTableMutation = useMutation({
    ...postApiTablesMutation(),
    onSuccess: () => {
      toast.success('จัดโต๊ะเรียบร้อยแล้ว')
      invalidate()
    },
    onError: (error) => {
      const apiError = error as unknown as ApiError
      console.error('Assign error:', apiError)
      const message =
        apiError.body?.error || apiError.message || 'เกิดข้อผิดพลาดในการจัดโต๊ะ'
      toast.error(`ดำเนินการไม่สำเร็จ: ${message}`)
    },
  })

  // Bulk Assignment Mutation (with Sync support)
  const bulkAssignMutation = useMutation({
    ...postApiTablesBulkMutation(),
    onSuccess: () => {
      toast.success(`อัปเดตรายชื่อแขกเรียบร้อยแล้ว`)
      invalidate()
      onClose()
      setSelectedMap(new Map())
    },
    onError: (error) => {
      const apiError = error as unknown as ApiError
      console.error('Bulk assign error:', apiError)
      const message =
        apiError.body?.error || apiError.message || 'เกิดข้อผิดพลาดในการจัดโต๊ะ'
      toast.error(`ดำเนินการไม่สำเร็จ: ${message}`)
    },
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getApiAdminCheckinsQueryKey() })
    queryClient.invalidateQueries({ queryKey: getApiAdminGuestQueryKey() })
    queryClient.invalidateQueries({
      queryKey: getApiTablesMasterByIdGuestsQueryKey({
        path: { id: tableId },
      }),
    })
    queryClient.invalidateQueries({ queryKey: getApiTablesMasterQueryKey() })
  }

  const handleSingleAssign = (guest: GuestItemData) => {
    setTableMutation.mutate({
      body: {
        targetId: guest.id,
        tableId: tableId,
      },
    })
  }

  const handleBulkSave = () => {
    const assignments = Array.from(selectedMap.values()).map((g) => ({
      targetId: g.targetId,
    }))

    bulkAssignMutation.mutate({
      body: {
        tableId: tableId,
        assignments,
        sync: true, // Reconcile assignments
      },
    })
  }

  const renderGuestItem = (
    guest: GuestItemData,
    variant: 'available' | 'selected',
  ) => {
    const isSelectedInMap = isSelected(guest.id)
    const isCheckedInStatus = guest.status === 'checked-in'
    const guestTableId = guest.tableAssignment?.tableId
    const isAlreadyInThisTable = guestTableId === tableId

    const guestCount = guest.followers || guest.expectedFollowers || 1
    const wouldExceedCapacity =
      variant === 'available' &&
      !isSelectedInMap &&
      totalSelectedFollowers + guestCount > capacity

    const isDisabled = variant === 'available' && wouldExceedCapacity

    return (
      <div
        key={guest.id}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-all ${
          isSelectedInMap && variant === 'available'
            ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
            : isDisabled
              ? 'bg-muted/20 cursor-not-allowed opacity-50 grayscale'
              : 'hover:bg-muted/50 border-transparent bg-white shadow-sm'
        }`}
        onClick={() => !isDisabled && toggleGuest(guest)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled)
            toggleGuest(guest)
        }}
      >
        <div className="flex items-center">
          {variant === 'available' ? (
            <Checkbox
              checked={isSelectedInMap}
              onCheckedChange={() => toggleGuest(guest)}
              disabled={isDisabled}
              className="size-5 rounded-md"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-destructive/10 hover:text-destructive size-8 rounded-full"
              onClick={(e) => {
                e.stopPropagation()
                toggleGuest(guest)
              }}
            >
              <UserMinus className="size-4" />
            </Button>
          )}
        </div>

        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm font-bold">{guest.fullName}</p>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="text-muted-foreground bg-muted rounded-md px-1.5 py-0.5 text-[10px] font-medium">
              {guest.followers || guest.expectedFollowers || 1} ท่าน
            </span>
            {guest.tableAssignment && (
              <span
                className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                  isAlreadyInThisTable
                    ? 'bg-primary/10 text-primary'
                    : 'bg-orange-100 text-orange-600'
                }`}
              >
                โต๊ะ {guest.tableAssignment.tableNumber}
              </span>
            )}
            {variant === 'available' && !isCheckedInStatus && (
              <span className="bg-destructive/10 text-destructive rounded-md px-1.5 py-0.5 text-[10px] font-bold">
                ยังไม่เช็กอิน
              </span>
            )}
          </div>
        </div>

        {isMobile && variant === 'available' && !isAlreadyInThisTable && (
          <Button
            size="icon"
            variant="ghost"
            className="rounded-full"
            onClick={(e) => {
              e.stopPropagation()
              handleSingleAssign(guest)
            }}
            disabled={setTableMutation.isPending || isDisabled}
          >
            <UserPlus className="size-4" />
          </Button>
        )}
      </div>
    )
  }

  const availableList = (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="relative mb-3">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          placeholder="ค้นหาชื่อแขก..."
          className="bg-muted/30 focus-visible:ring-primary/20 h-10 rounded-xl border-none pl-10 text-sm shadow-none focus-visible:ring-1"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-0.5">
        {isLoadingAll ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-muted h-16 w-full animate-pulse rounded-xl"
              />
            ))}
          </div>
        ) : filteredAvailableList.length > 0 ? (
          filteredAvailableList.map((guest) =>
            renderGuestItem(guest, 'available'),
          )
        ) : (
          <div className="py-12 text-center">
            <p className="text-muted-foreground text-sm">ไม่พบรายชื่อแขก</p>
          </div>
        )}

        <div
          ref={loadMoreRef}
          className="flex justify-center py-2"
        >
          {isFetchingNextPage && (
            <div className="border-primary size-4 animate-spin rounded-full border-2 border-t-transparent" />
          )}
        </div>
      </div>
    </div>
  )

  const selectedList = (
    <div className="flex flex-1 flex-col gap-2 overflow-hidden p-6">
      <div className="mb-3 flex shrink-0 items-center justify-between">
        <h4 className="text-sm font-bold">
          แขกในโต๊ะนี้ ({totalSelectedFollowers}/{capacity})
        </h4>
        {selectedMap.size > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive h-8 text-xs"
            onClick={() => setSelectedMap(new Map())}
          >
            ล้างทั้งหมด
          </Button>
        )}
      </div>

      {totalSelectedFollowers >= capacity && (
        <Alert
          variant="destructive"
          className="bg-destructive/5 text-destructive border-destructive/20 py-2"
        >
          <AlertCircle className="size-4" />
          <AlertDescription
          // className="text-[11px] font-bold"
          >
            ที่นั่งเต็มแล้ว ({capacity} ท่าน) ไม่สามารถเพิ่มได้อีก
          </AlertDescription>
        </Alert>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-0.5">
        {isLoadingExisting ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-muted h-16 w-full animate-pulse rounded-xl"
              />
            ))}
          </div>
        ) : Array.from(selectedMap.values()).length > 0 ? (
          Array.from(selectedMap.values()).map((g) =>
            renderGuestItem(
              {
                id: g.targetId,
                fullName: g.fullName,
                followers: g.followers,
              },
              'selected',
            ),
          )
        ) : (
          <div className="bg-muted/20 flex h-full flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center">
            <UserPlus className="text-muted-foreground/30 mb-2 size-8" />
            <p className="text-muted-foreground text-xs">
              ยังไม่มีแขกในโต๊ะนี้
              <br />
              เลือกรายชื่อจากด้านซ้ายเพื่อเพิ่ม
            </p>
          </div>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
      >
        <DrawerContent className="max-h-[96dvh]">
          <div className="mx-auto flex h-[90dvh] w-full max-w-lg flex-col overflow-hidden px-6">
            <DrawerHeader className="mb-4 flex flex-row items-center justify-between p-0 pt-6">
              <div>
                <DrawerTitle className="text-xl font-bold">
                  เพิ่มแขกลงโต๊ะ {tableNumber}
                </DrawerTitle>
                <DrawerDescription>
                  ที่นั่งว่าง: {capacity - totalSelectedFollowers} / {capacity}
                </DrawerDescription>
              </div>
              <DrawerClose className="bg-muted text-muted-foreground hover:text-foreground rounded-full p-2 transition-colors">
                <X className="size-5" />
              </DrawerClose>
            </DrawerHeader>

            {availableList}

            {selectedMap.size > 0 && (
              <div className="bg-background/80 sticky bottom-0 border-t pt-4 pb-8 backdrop-blur-sm">
                <Button
                  className="shadow-primary/20 w-full rounded-xl py-6 font-bold shadow-lg"
                  onClick={handleBulkSave}
                  disabled={
                    bulkAssignMutation.isPending ||
                    totalSelectedFollowers > capacity
                  }
                >
                  {bulkAssignMutation.isPending
                    ? 'กำลังบันทึก...'
                    : `ยืนยันจัดลงโต๊ะ (${totalSelectedFollowers} ท่าน)`}
                </Button>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="flex h-[85vh] max-w-4xl! flex-col gap-0 overflow-hidden border-none p-0 shadow-2xl">
        <DialogHeader className="shrink-0 border-b bg-white px-8 py-6">
          <div className="flex items-center justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <DialogTitle className="truncate text-2xl font-bold tracking-tight">
                  จัดที่นั่งโต๊ะ {tableNumber}
                </DialogTitle>
                <span className="bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-bold">
                  ความจุ {capacity} ท่าน
                </span>
              </div>
              <DialogDescription className="mt-1 truncate text-sm">
                ย้ายรายชื่อแขกไปมาเพื่อจัดที่นั่ง (เลือกได้หลายกลุ่ม)
              </DialogDescription>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-xl border-2 px-4 text-sm font-bold"
                onClick={onClose}
              >
                ยกเลิก
              </Button>
              <Button
                className="shadow-primary/20 h-10 rounded-xl px-6 text-sm font-bold shadow-lg"
                onClick={handleBulkSave}
                disabled={
                  bulkAssignMutation.isPending ||
                  isLoadingExisting ||
                  totalSelectedFollowers > capacity
                }
              >
                {bulkAssignMutation.isPending ? (
                  'กำลังบันทึก...'
                ) : (
                  <>
                    <Check className="mr-2 size-4" />
                    ยืนยัน ({totalSelectedFollowers} ท่าน)
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden bg-[#fbfbfb]">
          <div className="flex flex-[1.2] flex-col overflow-hidden p-6">
            {availableList}
          </div>

          <div className="flex flex-1 flex-col overflow-hidden border-l bg-white">
            {selectedList}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
